import { useCallback, useEffect, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'

function readStorage(key, fallback) {
    try {
        const value = window.localStorage.getItem(key)
        return value ? JSON.parse(value) : fallback
    } catch {
        return fallback
    }
}

function writeStorage(key, value) {
    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Storage may be unavailable in private/restricted browser contexts.
    }
}

function newestCreatedFirst(first, second) {
    return new Date(second.created_at || 0).getTime() - new Date(first.created_at || 0).getTime()
}

export function useCreatedCards(userId, companyId) {
    const [cards, setCards] = useState([])
    const [updatedCardIds, setUpdatedCardIds] = useState([])
    const [loading, setLoading] = useState(true)
    const storageScope = `${userId || 'anonymous'}:${companyId || 'company'}`
    const versionsKey = `kanbify:created-card-versions:${storageScope}`
    const updatesKey = `kanbify:created-card-updates:${storageScope}`

    const fetchCards = useCallback(async () => {
        if (!userId) {
            setCards([])
            setLoading(false)
            return
        }

        const { data, error } = await kanbify
            .from('cards')
            .select('id, title, is_completed, created_at, updated_at')
            .eq('created_by', userId)
            .order('created_at', { ascending: false })
            .limit(500)

        if (error) {
            logDevError('[useCreatedCards] fetch failed:', error)
            setLoading(false)
            return
        }

        const nextCards = data || []
        const previousVersions = readStorage(versionsKey, {})
        const knownUpdates = new Set(readStorage(updatesKey, []))
        const hasPreviousSnapshot = Object.keys(previousVersions).length > 0
        const nextVersions = {}
        const presentIds = new Set()

        nextCards.forEach((card) => {
            presentIds.add(card.id)
            const currentVersion = card.updated_at || card.created_at || ''
            nextVersions[card.id] = currentVersion
            if (
                hasPreviousSnapshot
                && previousVersions[card.id]
                && currentVersion
                && String(currentVersion) > String(previousVersions[card.id])
            ) {
                knownUpdates.add(card.id)
            }
        })

        const nextUpdates = Array.from(knownUpdates).filter((cardId) => presentIds.has(cardId))
        writeStorage(versionsKey, nextVersions)
        writeStorage(updatesKey, nextUpdates)
        setUpdatedCardIds(nextUpdates)
        setCards(nextCards)
        setLoading(false)
    }, [updatesKey, userId, versionsKey])

    useEffect(() => {
        const timeoutId = window.setTimeout(() => { void fetchCards() }, 0)
        return () => window.clearTimeout(timeoutId)
    }, [fetchCards])

    useEffect(() => {
        if (!userId) return undefined

        const channel = kanbify
            .channel(`created-cards:${storageScope}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'cards',
            }, () => { void fetchCards() })
            .subscribe()

        const intervalId = window.setInterval(() => { void fetchCards() }, 5000)

        return () => {
            window.clearInterval(intervalId)
            kanbify.removeChannel(channel)
        }
    }, [fetchCards, storageScope, userId])

    const markCardSeen = useCallback((cardId) => {
        if (!cardId) return
        setUpdatedCardIds((current) => {
            const next = current.filter((id) => id !== cardId)
            writeStorage(updatesKey, next)
            return next
        })
    }, [updatesKey])

    return {
        openCards: cards.filter((card) => !card.is_completed).sort(newestCreatedFirst),
        completedCards: cards.filter((card) => card.is_completed).sort(newestCreatedFirst),
        updatedCardIds,
        loading,
        refresh: fetchCards,
        markCardSeen,
    }
}
